"use client";
import NoAccessPlaceholder from "@/app/components/access/NoAccessPlaceholder";
import { ResourceNotAvailablePlaceholder } from "@/app/components/cards/ResourceNotAvailablePlaceholder";
import ResourceFilterAndBannerWrapper from "@/app/components/filters/ResourceFilterAndBannerWrapper";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { Box, NavLink, Paper, Select, Text } from "@mantine/core";
import MenuBookRounded from "@mui/icons-material/MenuBookRounded";
import { MainMetaMapSubTopic } from "@tsTypes/meta-schema";
import { Note, NoteSection, SubTopicNotes } from "@tsTypes/note-schema";
import { Resources } from "@tsTypes/subject-schema";
import { useCheckUserAccess } from "@utils/accessChecks";
import { getUserPreferredSubTopicTitle } from "@utils/routing";
import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { useMainMetaMap } from "../../../../../../../components/CoreDataProvider";
import LinkedTopicResourceBanner from "../../../../components/banners/LinkedTopicResourceBanner";
import RestrictedResourceBanner from "../../../../components/banners/RestrictedResourceBanner";
import { useSubTopicTitleStyles } from "../../../../components/titles/SubTopicTitle";
import classes from "./NoteLayout.module.css";
import NotePage from "./NotePage";
import notePageClasses from "./NotePage.module.css";

interface SectionNavItem {
  label: string;
  sectionIndex: number;
  subTopicDocumentId: string;
  note: Note;
  section: NoteSection;
  isSubTopicHeader?: boolean;
}

export default function NoteLayout({
  allNotes,
  subjectName,
  subjectDocumentId,
  topicDocumentId,
  subTopicDocumentId,
  linkedResource,
}: {
  allNotes: SubTopicNotes[] | Note | undefined;
  subjectName: string;
  subjectDocumentId: string;
  topicDocumentId: string;
  subTopicDocumentId?: string;
  linkedResource: Resources | null;
}) {
  const mainMetaMap = useMainMetaMap();
  const subTopicMeta = subTopicDocumentId
    ? (mainMetaMap[subTopicDocumentId] as MainMetaMapSubTopic)
    : undefined;
  const userAccess = useCheckUserAccess("notes", subjectDocumentId);
  const subjectNameLower = subjectName.toLowerCase();
  const subjectColorIndex = getSubjectColorIndex(subjectNameLower);
  const { classes: subTopicTitleClasses } = useSubTopicTitleStyles({
    subjectColorIndex,
  });

  const isSubTopicLevel = !!subTopicDocumentId;

  const sections = useMemo(() => {
    const items: SectionNavItem[] = [];

    if (isSubTopicLevel) {
      const note = allNotes as Note | undefined;
      if (!note) return items;
      note.NoteSection.forEach((section, sIdx) => {
        items.push({
          label: `${sIdx + 1}. ${section.Title}`,
          sectionIndex: sIdx,
          subTopicDocumentId: subTopicDocumentId!,
          note,
          section,
        });
      });
    } else {
      (allNotes as SubTopicNotes[]).forEach((subTopicNotes) => {
        if (!subTopicNotes.note) return;
        subTopicNotes.note.NoteSection.forEach((section, sIdx) => {
          items.push({
            label: `${sIdx + 1}. ${section.Title}`,
            sectionIndex: sIdx,
            subTopicDocumentId: subTopicNotes.documentId,
            note: subTopicNotes.note!,
            section,
            isSubTopicHeader: sIdx === 0,
          });
        });
      });
    }

    return items;
  }, [allNotes, subTopicDocumentId, isSubTopicLevel]);

  const [activeIndex, setActiveIndex] = useState(0);

  const handleSectionChange = useCallback(
    (idx: number) => {
      setActiveIndex(idx);
      const sectionId = sections[idx]?.section.customDocumentId;
      if (sectionId !== undefined) {
        requestAnimationFrame(() => {
          document
            .getElementById(sectionId.toString())
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    },
    [sections]
  );

  const hasNoContent = useMemo(() => {
    if (isSubTopicLevel) {
      return !(allNotes as Note | undefined);
    }
    return (allNotes as SubTopicNotes[]).every((s) => !s.note);
  }, [allNotes, isSubTopicLevel]);

  const activeSection = sections[activeIndex];
  const isSectionLocked =
    activeSection?.sectionIndex > 0 && userAccess !== "Full Access";

  const dropdownData = useMemo(() => {
    if (isSubTopicLevel) {
      return sections.map((s, idx) => ({
        label: s.label,
        value: idx.toString(),
      }));
    }
    const groups: { group: string; items: { value: string; label: string }[] }[] =
      [];
    sections.forEach((s, idx) => {
      const groupLabel = getUserPreferredSubTopicTitle(
        mainMetaMap[s.subTopicDocumentId] as MainMetaMapSubTopic
      );
      const item = { value: idx.toString(), label: s.label };
      const existing = groups.find((g) => g.group === groupLabel);
      if (existing) existing.items.push(item);
      else groups.push({ group: groupLabel, items: [item] });
    });
    return groups;
  }, [sections, isSubTopicLevel, mainMetaMap]);

  const selectVars = {
    "--note-select-bg": `light-dark(var(--mantine-color-${subjectColorIndex}-9), var(--mantine-color-${subjectColorIndex}-8))`,
    "--note-select-border": `light-dark(var(--mantine-color-${subjectColorIndex}-6), var(--mantine-color-${subjectColorIndex}-7))`,
    "--note-select-selected-bg": `var(--mantine-color-${subjectColorIndex}-6)`,
  } as React.CSSProperties;

  return (
    <>
      <ResourceFilterAndBannerWrapper hasFilters={false}>
        <RestrictedResourceBanner
          resource={"notes"}
          subjectDocumentId={subjectDocumentId}
          subjectName={subjectNameLower}
          userAccess={userAccess}
        />
      </ResourceFilterAndBannerWrapper>

      <Box className={classes.layoutContainer}>
        {/* Placeholder for no content */}
        {hasNoContent ? (
          <Paper
            withBorder
            className={clsx(notePageClasses.notePage, classes.noContentContainer)}
          >
            <ResourceNotAvailablePlaceholder
              reason="in development"
              resource="notes"
              subjectColorIndex={subjectColorIndex}
            />
          </Paper>
        ) : (
          <>
            {/* Mobile section selector */}
            <Select
              searchable
              data={dropdownData}
              value={activeIndex.toString()}
              onChange={(value) => handleSectionChange(Number(value ?? 0))}
              mt="md"
              mb="md"
              size="md"
              className={classes.mobileOnly}
              style={selectVars}
              classNames={{
                input: classes.selectInput,
                dropdown: classes.selectDropdown,
                option: classes.selectItem,
              }}
            />
            {/* Desktop left nav */}
            <Paper withBorder className={clsx(classes.sideNav, classes.desktopOnly)}>
              <Box className={classes.navHeader}>
                <MenuBookRounded fontSize="small" />
                <Text fw={600} size="sm">
                  Chapters
                </Text>
              </Box>
              <Box className={classes.navContent}>
                {sections.map((item, idx) => {
                  const subTopicTitle =
                    !isSubTopicLevel && item.isSubTopicHeader
                      ? getUserPreferredSubTopicTitle(
                          mainMetaMap[
                            item.subTopicDocumentId
                          ] as MainMetaMapSubTopic
                        )
                      : undefined;

                  return (
                    <Box
                      key={`${item.subTopicDocumentId}-${item.sectionIndex}`}
                    >
                      {subTopicTitle && (
                        <Text
                          component="div"
                          className={clsx(
                            subTopicTitleClasses.subTopicTitle,
                            classes.subTopicNavTitle,
                            idx === 0 && classes.firstSubTopicNavTitle
                          )}
                          dangerouslySetInnerHTML={{
                            __html: subTopicTitle.replace(
                              " (HL)",
                              "&nbsp;<span>(HL)</span>"
                            ),
                          }}
                        />
                      )}
                      <NavLink
                        label={item.label}
                        active={idx === activeIndex}
                        onClick={() => handleSectionChange(idx)}
                        className={classes.navLink}
                        variant="filled"
                      />
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            {/* Content area */}
            {isSectionLocked ? (
              <Paper withBorder className={notePageClasses.notePage}>
                <Text>
                  <NoAccessPlaceholder />
                </Text>
              </Paper>
            ) : activeSection ? (
              <NotePage
                sectionContent={activeSection.section}
                pageDocumentId={activeSection.note.documentId}
                subTopicDocumentId={activeSection.subTopicDocumentId}
                subjectNameLower={subjectNameLower}
                topicDocumentId={topicDocumentId}
                subjectDocumentId={subjectDocumentId}
                disableActions={userAccess !== "Full Access"}
              />
            ) : null}
          </>
        )}
      </Box>

      <LinkedTopicResourceBanner
        subject={subjectNameLower}
        currentResource={"notes"}
        linkedResource={linkedResource}
        topicDocumentId={topicDocumentId}
        subTopicDocumentId={subTopicDocumentId}
        subTopicTitle={subTopicMeta?.title}
      />
    </>
  );
}
